/* eslint-disable no-param-reassign */
import React, { useContext, useEffect, useReducer } from 'react';
import produce from 'immer';
import { loadStripe } from '@stripe/stripe-js';

import {
  AppInfo,
  Application,
  AccountApps,
  PlanEnum,
  AppStatusEnum,
} from 'api-bible-shared-lib/utils/types';
import { useAccountUser } from './User';

import * as portalApi from '../utils/api';

interface ApplicationsById {
  [id: string]: Application;
}

interface ApplicationsState {
  loaded: boolean;
  applications: Application[];
  defaultApplicationId: string;
  maxFreeAppsAllowed: number;
}

interface ApplicationsContextParams extends Omit<ApplicationsState, 'applications'> {
  applicationsById: ApplicationsById;
  createApplication: (appFormData: AppInfo) => Promise<Application>;
  editApplication: (appId: string, data: Partial<Application>) => Promise<Application>;
  cancelApplicationPlan: (appId: string, status: AppStatusEnum) => Promise<Application>;
  changeApplicationPlan: (appId: string, plan: PlanEnum, newPlan: PlanEnum) => Promise<Application>;
  manageApplicationBilling: (appId: string) => Promise<void>;
  redirectToCheckout: (appId: string) => Promise<void>;
}

type ApplicationsAction =
  | { type: 'load'; payload: AccountApps }
  | { type: 'create'; payload: Application }
  | { type: 'update'; payload: Application };

const defaultApplicationsState: ApplicationsState = {
  loaded: false,
  applications: [],
  maxFreeAppsAllowed: 1,
  defaultApplicationId: '',
};

const defaultApplicationsContextParams: ApplicationsContextParams = {
  ...defaultApplicationsState,
  applicationsById: {},
  editApplication: () => Promise.reject(),
  createApplication: () => Promise.reject(),
  cancelApplicationPlan: () => Promise.reject(),
  changeApplicationPlan: () => Promise.reject(),
  manageApplicationBilling: () => Promise.reject(),
  redirectToCheckout: () => Promise.reject(),
};

export const ApplicationsContext = React.createContext(defaultApplicationsContextParams);

const applicationsReducer = produce((draft: ApplicationsState, action: ApplicationsAction) => {
  switch (action.type) {
    case 'load': {
      draft.loaded = true;
      draft.defaultApplicationId = action.payload.apps[0]?.id || '';
      draft.applications = action.payload.apps;
      break;
    }
    case 'create':
      if (!draft.defaultApplicationId) {
        draft.defaultApplicationId = action.payload.id;
      }
      draft.applications.push(action.payload);
      break;
    case 'update':
      // eslint-disable-next-line no-case-declarations
      const index = draft.applications.findIndex((app) => app.id === action.payload.id);
      if (index >= 0) {
        draft.applications[index] = action.payload;
      } else {
        draft.applications.push(action.payload);
      }
      break;
    default:
      // eslint-disable-next-line no-console
      console.error('unexpected action received by applicationsReducer', action);
      break;
  }
});

export const ApplicationsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const [state, dispatch] = useReducer(applicationsReducer, defaultApplicationsState);
  const { account } = useAccountUser();

  useEffect(() => {
    if (account?.id) {
      portalApi
        .getAccountApps({ accountId: account.id })
        .then((accountApps) => {
          dispatch({ type: 'load', payload: accountApps });
        })
        // eslint-disable-next-line no-console
        .catch((err) => console.error(err));
    }
  }, [account]);

  const createApplication = async (data: AppInfo): Promise<Application> => {
    if (!account) throw new Error('must be signed in to create application');
    const application = await portalApi.postAccountApp({ accountId: account.id, data });

    if (application.plan !== PlanEnum.FREE) {
      await redirectToCheckout(application.id);
    }

    dispatch({ type: 'create', payload: application });
    return application;
  };

  const editApplication = async (
    appId: string,
    data: Partial<Application>,
  ): Promise<Application> => {
    if (!account) throw new Error('must be signed in to create application');
    const application = await portalApi.putAccountApp({ appId, accountId: account.id, data });
    dispatch({ type: 'update', payload: application });
    return application;
  };

  const redirectToCheckout = async (appId: string) => {
    if (!account) throw new Error('user must be signed in to upgradeApplication');

    const { sessionId, publishableKey } = await portalApi.postCheckout(account.id, appId);

    const stripe = await loadStripe(publishableKey);
    if (!stripe) {
      throw new Error('unable to load Stripe');
    }

    await stripe.redirectToCheckout({ sessionId });
  };

  const cancelApplicationPlan = async (
    appId: string,
    status: AppStatusEnum,
  ): Promise<Application> => {
    if (!account) throw new Error("user must be signed in to cancel an application's plan");

    const accountId = account.id;
    const application = await portalApi.patchAccountApp({
      accountId,
      appId,
      data: { status },
      newData: { status: AppStatusEnum.CANCELED },
    });

    dispatch({ type: 'update', payload: application });
    return application;
  };

  const changeApplicationPlan = async (
    appId: string,
    plan: PlanEnum,
    newPlan: PlanEnum,
  ): Promise<Application> => {
    if (!account) throw new Error("user must be signed in to change an application's plan");
    if (plan === newPlan)
      throw new Error("specify a different plan than the application's current plan");

    const accountId = account.id;
    const application = await portalApi.patchAccountApp({
      accountId,
      appId,
      data: { plan },
      newData: { plan: newPlan },
    });

    if (application.plan !== PlanEnum.FREE) {
      await redirectToCheckout(application.id);
    }

    dispatch({ type: 'update', payload: application });
    return application;
  };

  const manageApplicationBilling = async (appId: string) => {
    if (!account) throw new Error('user must be signed in to upgradeApplication');

    const stripeCustomerPortalUrl = await portalApi.getStripeCustomerPortalUrl(account.id, appId);
    window.location.href = stripeCustomerPortalUrl.url;
  };

  return (
    <ApplicationsContext.Provider
      value={{
        ...state,
        applicationsById: state.applications.reduce((appsById: ApplicationsById, app) => {
          const newAppsById = { ...appsById };
          newAppsById[app.id] = JSON.parse(JSON.stringify(app)); // avoid deep clone issues of state
          return newAppsById;
        }, {}),
        editApplication,
        createApplication,
        cancelApplicationPlan,
        changeApplicationPlan,
        manageApplicationBilling,
        redirectToCheckout,
      }}
    >
      {children}
    </ApplicationsContext.Provider>
  );
};

export const useApplications = (): ApplicationsContextParams => useContext(ApplicationsContext);
